// Brand Controls redesign — shell.
//
// Shared ModuleHeader band (with in-band tabs) + view routing + the shared
// empty-state. All state and handlers live in the orchestrator
// (brand-voice-profile.tsx); this is presentational.

import { Layers } from "lucide-react";
import {ModuleHeader} from "../../shell/module-header";
import { EmptyState } from "./shared";
import { OverviewView, type OverviewViewProps } from "./overview-view";
import { QuickSetupView, type QuickSetupViewProps } from "./quick-setup-view";
import { AdvancedView, type AdvancedViewProps } from "./advanced-view";
import { HistoryView, type HistoryViewProps } from "./history-view";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "setup", label: "Quick Setup" },
  { value: "advanced", label: "Advanced Editing" },
  { value: "history", label: "Version History" },
];

export interface BrandControlsShellProps {
  activeTab: string;
  setActiveTab: (t: string) => void;
  hasProfile: boolean;
  brand: string;
  headerVersion: string;
  headerUpdatedAt: string;
  headerUsageCount: number;
  goSetup: () => void;
  overview: OverviewViewProps;
  quickSetup: QuickSetupViewProps;
  advanced: AdvancedViewProps;
  history: HistoryViewProps;
}

// Match the app gutter (Deep Signal / Brand Monitoring land at 64px on
// desktop). px-6 md:px-16 = 24px mobile / 64px desktop.
const GUTTER = "px-6 md:px-16";
const PANEL_PAD = `${GUTTER} py-6 md:py-8`;

export function BrandControlsShell(props: BrandControlsShellProps) {
  const { activeTab, setActiveTab, hasProfile, brand, goSetup } = props;

  return (
    <div style={{ backgroundColor: "var(--background)" }}>
      <ModuleHeader
        page="Brand Controls"
        icon={Layers}
        title={`Teach AI your voice. Every fix sounds like ${brand}.`}
        description="This is the voice every generated asset inherits, across Action Items and Test & Refine. Set it once and AI writes as you, not as a generic model."
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* panels (tab nav lives in the header band) */}
        {activeTab === "overview" && (
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <OverviewView {...props.overview} />
            ) : (
              <EmptyState
                headline={`No voice yet. Let's teach AI how ${brand} sounds.`}
                body="Point me at your site and I'll read your live content, detect how you actually write, and draft a profile in about 30 seconds. Every asset AI generates will inherit it."
                onExtract={goSetup}
              />
            )}
          </div>
        )}

        {activeTab === "setup" && (
          <div className={PANEL_PAD}>
            <QuickSetupView {...props.quickSetup} />
          </div>
        )}

        {activeTab === "advanced" && (
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <AdvancedView {...props.advanced} />
            ) : (
              <EmptyState
                headline="Nothing to fine-tune yet"
                body="Extract a voice first, then come back here to adjust each attribute, edit the avoid list, and test how it reads."
                onExtract={goSetup}
              />
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className={PANEL_PAD}>
            {hasProfile ? (
              <HistoryView {...props.history} />
            ) : (
              <EmptyState
                headline="No versions yet"
                body="Once you extract and save a voice, every version shows up here so you can compare changes or roll back to an earlier one."
                onExtract={goSetup}
              />
            )}
          </div>
        )}
    </div>
  );
}
